import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Calendar, Clock, User, Video, Phone, MessageSquare, Plus, Filter, Search, X, Edit2, Stethoscope } from 'lucide-react';
import { API_BASE_URL } from '../config/api';
import toast from 'react-hot-toast';

const Appointments = () => {
  const { user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    mode: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });
  const [cancellingId, setCancellingId] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  
  // Image loading state - must be at top level
  const backgroundImageUrl = `${API_BASE_URL}/uploads/backgrounds/appointment.jpg`;
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  // Preload image to check if it's accessible
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setImageLoaded(true);
      setImageError(false);
      console.log('Appointments background image loaded successfully:', backgroundImageUrl);
    };
    img.onerror = () => {
      setImageError(true);
      setImageLoaded(false);
      console.error('Failed to load appointments background image:', backgroundImageUrl);
    };
    img.src = backgroundImageUrl;
  }, [backgroundImageUrl]);

  useEffect(() => {
    fetchAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.type, filters.mode, pagination.currentPage]);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      
      // Build query params, only including non-empty filter values
      const queryParams = new URLSearchParams({
        page: pagination.currentPage.toString(),
        limit: pagination.itemsPerPage.toString()
      });
      
      // Add filters only if they have values
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.type) queryParams.append('type', filters.type);
      if (filters.mode) queryParams.append('mode', filters.mode);

      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/appointments?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAppointments(data.appointments || []);
        setPagination(data.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          itemsPerPage: 10
        });
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Failed to fetch appointments' }));
        console.error('Error fetching appointments:', errorData);
        // Set empty state on error
        setAppointments([]);
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Search is handled by filteredAppointments
  };

  const handleCancelAppointment = async () => {
    if (!appointmentToCancel) return;

    try {
      setCancellingId(appointmentToCancel._id);
      const response = await fetch(`${API_BASE_URL}/api/appointments/${appointmentToCancel._id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          cancellationReason: cancelReason || 'No reason provided'
        })
      });

      if (response.ok) {
        toast.success('Appointment cancelled successfully');
        setShowCancelModal(false);
        setAppointmentToCancel(null);
        setCancelReason('');
        fetchAppointments(); // Refresh the list
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.message || 'Failed to cancel appointment');
      }
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      toast.error('An error occurred while cancelling the appointment');
    } finally {
      setCancellingId(null);
    }
  };

  const openCancelModal = (appointment) => {
    if (appointment.status === 'in-progress' || appointment.status === 'completed') {
      toast.error('Cannot cancel appointment that is in progress or completed');
      return;
    }
    if (appointment.status === 'cancelled') {
      toast.error('Appointment is already cancelled');
      return;
    }
    setAppointmentToCancel(appointment);
    setShowCancelModal(true);
  };

  const canReschedule = (appointment) => {
    return appointment.status !== 'in-progress' && 
           appointment.status !== 'completed' && 
           appointment.status !== 'cancelled';
  };

  const canCancel = (appointment) => {
    return appointment.status !== 'in-progress' && 
           appointment.status !== 'completed' && 
           appointment.status !== 'cancelled';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'in-progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'no-show':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getModeIcon = (mode) => {
    switch (mode) {
      case 'video':
        return <Video className="h-4 w-4" />;
      case 'audio':
        return <Phone className="h-4 w-4" />;
      case 'chat':
        return <MessageSquare className="h-4 w-4" />;
      default:
        return <MessageSquare className="h-4 w-4" />;
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (time) => {
    return time;
  };

  const canJoinCall = (appointment) => {
    const now = new Date();
    const appointmentDate = new Date(appointment.appointmentDate);
    const appointmentTime = new Date(appointmentDate);
    const [hours, minutes] = appointment.startTime.split(':');
    appointmentTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    
    // Allow joining 5 minutes before and 30 minutes after start time
    const fiveMinutesBefore = new Date(appointmentTime.getTime() - 5 * 60 * 1000);
    const thirtyMinutesAfter = new Date(appointmentTime.getTime() + 30 * 60 * 1000);
    
    return now >= fiveMinutesBefore && now <= thirtyMinutesAfter && 
           appointment.status === 'confirmed' && 
           appointment.mode === 'video';
  };

  const filteredAppointments = appointments.filter(appointment => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    const patientName = appointment.patient?.name?.toLowerCase() || '';
    const doctorName = appointment.doctor?.name?.toLowerCase() || '';
    const type = appointment.type?.toLowerCase() || '';
    
    return patientName.includes(searchLower) || 
           doctorName.includes(searchLower) || 
           type.includes(searchLower);
  });

  // Fallback background color
  const backgroundStyle = imageError 
    ? { backgroundColor: '#3b82f6', minHeight: '250px' }
    : {
        backgroundImage: imageLoaded 
          ? `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${backgroundImageUrl})`
          : 'linear-gradient(rgba(59, 130, 246, 0.6), rgba(59, 130, 246, 0.6))',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        minHeight: '250px'
      };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section with Background */}
      <div 
        className="relative py-16 mb-8"
        style={backgroundStyle}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">Appointments</h1>
              <p className="text-xl text-white/90">
                Manage your healthcare appointments and consultations
              </p>
            </div>
            <div className="mt-4 sm:mt-0">
              <Link
                to="/appointments/new"
                className="btn-primary inline-flex items-center space-x-2 bg-white text-primary-600 hover:bg-gray-100"
              >
                <Plus className="h-5 w-5" />
                <span>Book Appointment</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">

        {/* Filters and Search */}
        <div className="card mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            {/* Search */}
            <form onSubmit={handleSearch} className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search appointments..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input-field pl-10 w-full"
                />
              </div>
            </form>

            {/* Filters */}
            <div className="flex flex-wrap gap-4">
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="input-field min-w-[120px]"
              >
                <option value="">All Status</option>
                <option value="scheduled">Scheduled</option>
                <option value="confirmed">Confirmed</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
                <option value="no-show">No Show</option>
              </select>

              <select
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                className="input-field min-w-[140px]"
              >
                <option value="">All Types</option>
                <option value="consultation">Consultation</option>
                <option value="follow-up">Follow-up</option>
                <option value="emergency">Emergency</option>
                <option value="routine">Routine</option>
              </select>

              <select
                value={filters.mode}
                onChange={(e) => handleFilterChange('mode', e.target.value)}
                className="input-field min-w-[120px]"
              >
                <option value="">All Modes</option>
                <option value="video">Video</option>
                <option value="audio">Audio</option>
                <option value="chat">Chat</option>
              </select>
            </div>
          </div>
        </div>

        {/* Appointments List */}
        <div className="card">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <p className="mt-4 text-gray-600">Loading appointments...</p>
            </div>
          ) : filteredAppointments.length > 0 ? (
            <div className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Appointment
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date & Time
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type & Mode
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredAppointments.map((appointment) => (
                      <tr key={appointment._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                                {user?.userType === 'patient' ? (
                                  <Stethoscope className="h-6 w-6 text-primary-600" />
                                ) : (
                                  <User className="h-6 w-6 text-primary-600" />
                                )}
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {user?.userType === 'patient' 
                                  ? appointment.doctor?.name || 'Unknown Doctor'
                                  : appointment.patient?.name || 'Unknown Patient'
                                }
                              </div>
                              <div className="text-sm text-gray-500">
                                {user?.userType === 'patient' 
                                  ? (appointment.doctor?.doctorProfile?.specialization || 'Doctor')
                                  : 'Patient'
                                }
                              </div>
                              {user?.userType === 'patient' && appointment.doctor?.doctorProfile?.specialization && (
                                <div className="text-xs text-gray-400 mt-1">
                                  {appointment.doctor.doctorProfile.specialization}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 font-medium">
                            {formatDate(appointment.appointmentDate)}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center mt-1">
                            <Clock className="h-4 w-4 mr-1" />
                            {formatTime(appointment.startTime)} - {formatTime(appointment.endTime)}
                          </div>
                          {appointment.duration && (
                            <div className="text-xs text-gray-400 mt-1">
                              Duration: {appointment.duration} min
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 capitalize font-medium">
                            {appointment.type}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center mt-1">
                            {getModeIcon(appointment.mode)}
                            <span className="ml-1 capitalize">{appointment.mode}</span>
                          </div>
                          {appointment.payment?.amount && (
                            <div className="text-xs text-gray-400 mt-1">
                              ${appointment.payment.amount}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(appointment.status)}`}>
                            {appointment.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm font-medium">
                          <div className="flex flex-col space-y-2">
                            <div className="flex items-center space-x-2 flex-wrap">
                              {canJoinCall(appointment) && (
                                <Link
                                  to={`/video-call/${appointment._id}`}
                                  className="btn-primary text-xs px-3 py-1 whitespace-nowrap"
                                >
                                  Join Call
                                </Link>
                              )}
                              {canReschedule(appointment) && (
                                <Link
                                  to={`/appointments/${appointment._id}/reschedule`}
                                  className="inline-flex items-center text-xs px-3 py-1 text-blue-600 hover:text-blue-800 border border-blue-300 rounded hover:bg-blue-50 whitespace-nowrap"
                                >
                                  <Edit2 className="h-3 w-3 mr-1" />
                                  Reschedule
                                </Link>
                              )}
                              {canCancel(appointment) && (
                                <button
                                  onClick={() => openCancelModal(appointment)}
                                  disabled={cancellingId === appointment._id}
                                  className="inline-flex items-center text-xs px-3 py-1 text-red-600 hover:text-red-800 border border-red-300 rounded hover:bg-red-50 whitespace-nowrap disabled:opacity-50"
                                >
                                  <X className="h-3 w-3 mr-1" />
                                  {cancellingId === appointment._id ? 'Cancelling...' : 'Cancel'}
                                </button>
                              )}
                            </div>
                            <Link
                              to={`/appointments/${appointment._id}`}
                              className="text-primary-600 hover:text-primary-900 text-xs"
                            >
                              View Details →
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No appointments found</h3>
              <p className="text-gray-600 mb-4">
                {searchTerm || Object.values(filters).some(f => f) 
                  ? 'Try adjusting your search or filters'
                  : 'Get started by booking your first appointment'
                }
              </p>
              {!searchTerm && !Object.values(filters).some(f => f) && (
                <Link to="/appointments/new" className="btn-primary">
                  Book Appointment
                </Link>
              )}
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between mt-6">
            <div className="text-sm text-gray-700">
              Showing {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} to{' '}
              {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of{' '}
              {pagination.totalItems} results
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                disabled={pagination.currentPage === 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-3 py-2 text-sm font-medium text-gray-700">
                Page {pagination.currentPage} of {pagination.totalPages}
              </span>
              <button
                onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                disabled={pagination.currentPage === pagination.totalPages}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Cancel Appointment Modal */}
      {showCancelModal && appointmentToCancel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Cancel Appointment</h3>
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setAppointmentToCancel(null);
                  setCancelReason('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Are you sure you want to cancel this appointment?
              </p>
              <div className="bg-gray-50 p-3 rounded-lg mb-4">
                <p className="text-sm font-medium text-gray-900">
                  {user?.userType === 'patient' 
                    ? appointmentToCancel.doctor?.name 
                    : appointmentToCancel.patient?.name
                  }
                </p>
                <p className="text-xs text-gray-600">
                  {formatDate(appointmentToCancel.appointmentDate)} at {appointmentToCancel.startTime}
                </p>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for cancellation (optional)
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
                placeholder="Please provide a reason..."
                className="input-field w-full"
                maxLength={200}
              />
            </div>

            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setAppointmentToCancel(null);
                  setCancelReason('');
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={cancellingId === appointmentToCancel._id}
              >
                Keep Appointment
              </button>
              <button
                onClick={handleCancelAppointment}
                disabled={cancellingId === appointmentToCancel._id}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {cancellingId === appointmentToCancel._id ? 'Cancelling...' : 'Cancel Appointment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Appointments;
